import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { manualAttendanceFormSchema, type ManualAttendanceFormValues } from '@/schemas/attendance';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useUI } from '@/hooks/ui';
import { TIME_INPUT_REGEX } from '@/utils';
import { attendanceService } from '@/api/domains/attendance';
import { injectValidationErrors } from '@/utils/formValidationErrors';
import { useEmployeeSelector } from '@/hooks/employee/useEmployeeSelector';
import { useLocationSelector } from '../common/useLocationSelector';
import { useServerTimeSync } from '@/hooks/ui/useServerTimeSync';


interface UseManualAttendanceFormParams {
  onSubmit?: () => void;
  readOnly?: boolean;
}

export function useManualAttendanceForm({
  onSubmit: onSubmitCallback,
  readOnly,
}: UseManualAttendanceFormParams = {}) {
  const { t: translate } = useTranslation();
  const { setLoading } = useSidePanelLoading();
  const { showNotification, showApiError, closeSidePanel } = useUI();
  const effectiveReadOnly = !!readOnly;

  // Use specialized hooks
  const {
    employeeId,
    employeeOptions,
    isLoadingEmployees,
    employeeHasMore,
    handleEmployeeChange,
    handleEmployeeSearch,
    loadMoreEmployees,
  } = useEmployeeSelector();

  const {
    locationId,
    locationOptions,
    isLoadingLocations,
    handleLocationChange,
  } = useLocationSelector();

  const { maxDate, fallbackDate } = useServerTimeSync();

  // Local state for form fields
  const [attendanceDateValue, setAttendanceDateValue] = useState<string>(fallbackDate);
  const [attendanceTimeValue, setAttendanceTimeValue] = useState<string>('');
  const [notesValue, setNotesValue] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    setError,
    register,
    unregister,
    formState,
  } = useForm<ManualAttendanceFormValues>({
    resolver: zodResolver(manualAttendanceFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeId: '',
      locationId: '',
      attendanceDate: attendanceDateValue,
      attendanceTime: '',
      notes: '',
    },
  });

  // Field mapping for backend validation errors
  const fieldMapping = useMemo(() => ({
    employeeId: { formInstance: { setError } },
    locationId: { formInstance: { setError } },
    attendanceDate: { formInstance: { setError } },
    attendanceTime: { formInstance: { setError } },
    notes: { formInstance: { setError } },
    attendanceDateTime: { formInstance: { setError } },
    observation: { formInstance: { setError } },
  }), [setError]);

  // Handle date change
  const handleDateChange = useCallback((newDate: string) => {
    setAttendanceDateValue(newDate);
  }, []);

  // Handle time change
  const handleTimeChange = useCallback((newTime: string) => {
    if (TIME_INPUT_REGEX.test(newTime) || newTime === '') {
      setAttendanceTimeValue(newTime);
    }
  }, []);

  // Handle notes change
  const handleNotesChange = useCallback((value: string) => {
    setNotesValue(value);
  }, []);

  // Register and sync form fields
  useEffect(() => {
    register('employeeId');
    register('locationId');
    register('attendanceDate');
    register('attendanceTime');
    register('notes');
    return () => {
      unregister('employeeId');
      unregister('locationId');
      unregister('attendanceDate');
      unregister('attendanceTime');
      unregister('notes');
    };
  }, [register, unregister]);

  useEffect(() => {
    setValue('employeeId', employeeId);
  }, [employeeId, setValue]);

  useEffect(() => {
    setValue('locationId', locationId);
  }, [locationId, setValue]);

  useEffect(() => {
    setValue('attendanceDate', attendanceDateValue);
  }, [attendanceDateValue, setValue]);

  useEffect(() => {
    setValue('attendanceTime', attendanceTimeValue);
  }, [attendanceTimeValue, setValue]);

  useEffect(() => {
    setValue('notes', notesValue);
  }, [notesValue, setValue]);

  // Track if form is ready to submit
  const fieldsRegisteredRef = useRef(false);
  useEffect(() => {
    fieldsRegisteredRef.current = true;
    return () => { fieldsRegisteredRef.current = false; };
  }, []);

  const isReady = useMemo(() => {
    const hasEmployee = typeof employeeId === 'string' && employeeId.length > 0;
    const hasLocation = typeof locationId === 'string' && locationId.length > 0;
    const hasDate = typeof attendanceDateValue === 'string' && attendanceDateValue.length > 0;
    const hasTime = typeof attendanceTimeValue === 'string' && attendanceTimeValue.length > 0;
    const hasNotes = typeof notesValue === 'string';
    return (
      fieldsRegisteredRef.current && hasEmployee && hasLocation &&
      hasDate && hasTime && hasNotes
    );
  }, [
    employeeId,
    locationId,
    attendanceDateValue,
    attendanceTimeValue,
    notesValue,
  ]);

  // Track if the initial catalog load has completed
  // (prevents search queries from triggering the panel loading overlay)
  const initialLoadDoneRef = useRef(false);

  // Track if all data has loaded for initial display
  const areCatalogsLoaded = useMemo(() => (
    !isLoadingEmployees && !isLoadingLocations && 
    employeeOptions.length > 0 && locationOptions.length > 0
  ), [isLoadingEmployees, isLoadingLocations, employeeOptions.length, locationOptions.length]);
  
  // Report loading state to SidePanel - only during the initial load, not during search queries
  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // Submit manual attendance
  const submitManualAttendance = useCallback(async (data: ManualAttendanceFormValues) => {
    if (effectiveReadOnly) return;
    setIsSubmitting(true);

    try {
      const employeeIdNum = Number(data.employeeId);
      const locationIdNum = Number(data.locationId);

      // Combine date and time into ISO 8601 format without timezone (local)
      // Normalize time to always include seconds (HH:mm → HH:mm:ss)
      const timeWithSeconds = data.attendanceTime.length === 5
        ? `${data.attendanceTime}:00`
        : data.attendanceTime;
      const attendanceDateTime = `${data.attendanceDate}T${timeWithSeconds}`;

      const response = await attendanceService.registerManual({
        employeeId: employeeIdNum,
        locationId: locationIdNum,
        attendanceDateTime,
        observation: data.notes || null,
        attendanceType: null,
        latitude: null,
        longitude: null,
        eventId: null,
        note: null,
        deviceInfo: null,
        controlId: null,
        mode: null,
      });

      if (response.success) {
        showNotification('success', translate('attendance:manual.messages.success'), 5000);
        onSubmitCallback?.();
        closeSidePanel?.();
      } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
        injectValidationErrors(
          response.error.validationErrors,
          fieldMapping,
        );
        showNotification('error', translate('common:errors.validationErrors'), 4000);
      } else {
        showApiError(response);
      }
    } catch {
      showNotification('error', translate('common:errors.unexpected'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    showNotification, showApiError, onSubmitCallback,
    closeSidePanel, effectiveReadOnly, translate, fieldMapping,
  ]);

  // Handle form submission
  const onValid = useCallback(async (data: ManualAttendanceFormValues) => {
    await submitManualAttendance(data);
  }, [submitManualAttendance]);

  const onInvalid = useCallback((): void => {
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (effectiveReadOnly) return;
      const wrappedValid = (data: ManualAttendanceFormValues) => { void onValid(data); };
      const wrappedInvalid = () => { onInvalid(); };
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid, onInvalid, effectiveReadOnly],
  );

  return {
    employeeId,
    locationId,
    attendanceDate: attendanceDateValue,
    attendanceTime: attendanceTimeValue,
    notes: notesValue,
    maxDate,
    handleEmployeeChange,
    handleLocationChange,
    handleDateChange,
    handleTimeChange,
    handleNotesChange,
    employeeOptions,
    locationOptions,
    employeeHasMore,
    loadMoreEmployees,
    handleEmployeeSearch,
    isLoadingEmployees,
    isLoadingLocations,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    submitManualAttendance,
    errors: formState.errors,
    isSubmitted: formState.isSubmitted,
  } as const;
}
